import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createPerformanceCumulativeMultiDateQuery } from "gatherers/multi-date/cumulative-performance"
import { transformPerformanceData, useCurrentPerformanceItems, resolvePerformanceDrilldownSeed, createFactorItems } from "connected-components/graph/hooks/use-performance-items"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import type { ModelMetadata } from "resources/models"
import { useGraphData } from "../../hooks/use-graph-data"
import { getIntradayChartMode, useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { useIsRfrAttributionEnabled } from "resources/account"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_PERFORMANCE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { isIntradaySimulationEligible } from "gatherers/helpers"
import { getPerformanceApiStartDate } from "helpers/dates"
import { useSourceGraphMetricIds } from "../../hooks/use-source-graph-metric-ids"
import { useGraphAddSeries } from "../../hooks/use-graph-add-series"
import { buildCustomGraphAvailableMetrics } from "../custom/helpers"
import { getResolvableAddedMetrics } from "../custom/family"

const CATEGORIES_WITH_NO_INDIVIDUAL_FACTOR_ITEMS = ["total", "factors"]

interface PerformanceItem {
  id: string
  type: string
  disabled: boolean
}

function getSourcedIndividualFactorIds(currentItems: PerformanceItem[], selectedCategory: string): string[] | null {
  if (CATEGORIES_WITH_NO_INDIVIDUAL_FACTOR_ITEMS.includes(selectedCategory)) {
    return null
  }
  return currentItems.filter((item) => item.type === "factors" && !item.disabled).map((item) => item.id)
}

interface PerformanceGraphProps {
  modelMetadata: ModelMetadata
  onMetricsChange?: SourceMetricIdsChangeHandler
}

export default function PerformanceGraph({ modelMetadata, onMetricsChange }: PerformanceGraphProps) {
  const { startDate, performanceStartDate, performanceEndDate, base, filter, normalization, instrument, comparisonInstrument, secondInstrument, isEndVsStartDateComparison, disabledItems, setDisabledItems, setOnlyItem, enableItems, intradayBatchMetadata, intradayPricingAsOf, instrumentsMetadata, isIntradayMode, period, latestDate, dateOverride, isDateOverrideActive, addedMetrics, addMetric, seriesColorSlots, setSeriesColorSlots, selectedTemplate } = useGraphData()
  const { selectedCategory } = useGraphTemplateChart(GRAPH_PERFORMANCE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const baseWithSegment = useBaseWithSegment(base, filter, normalization)
  const segment = useFilteredSegment(filter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const categories = useMemo(() => {
    return modelMetadata.categories.map(({ id, label, color }) => ({
      id,
      label,
      color,
    }))
  }, [modelMetadata])

  const factorsMetadata = useMemo(() => {
    return modelMetadata.factors
  }, [modelMetadata])

  const builtInItems = useCurrentPerformanceItems({
    categories,
    factorsMetadata,
    disabledItems,
    selectedCategory,
    isRfrAttributionEnabled,
  })

  // The single performance query already returns every factor, so an added series is purely an extra row to plot.
  const currentItems = useMemo(() => {
    if (!addedMetrics.length) return builtInItems
    // Dedup against every built-in item regardless of disabled/hidden state, or a hidden one re-appends as a second, colliding entry.
    const builtInIds = new Set(builtInItems.map(item => item.id))
    const addedFactorItems = createFactorItems(factorsMetadata, disabledItems)
      .filter(item => addedMetrics.includes(item.id) && !builtInIds.has(item.id))
    return addedFactorItems.length ? [...builtInItems, ...addedFactorItems] : builtInItems
  }, [builtInItems, addedMetrics, factorsMetadata, disabledItems])

  const enabledIndividualFactorIds = useMemo(
    () => getSourcedIndividualFactorIds(currentItems, selectedCategory),
    [currentItems, selectedCategory],
  )
  useSourceGraphMetricIds({ itemIds: enabledIndividualFactorIds, onMetricsChange })

  const availableMetricsGroups = useMemo(() => buildCustomGraphAvailableMetrics(modelMetadata), [modelMetadata])
  const enabledBuiltInItemIds = useMemo(() => builtInItems.filter(item => !item.disabled).map(item => item.id), [builtInItems])
  // builtInItems never includes addedMetrics, so no extra filtering is needed here.
  const builtInItemIds = useMemo(() => builtInItems.map(item => item.id), [builtInItems])
  const chartColors = useThemeChartColors()
  const { addSeriesMetrics, alreadyShownMetricIds, onAddSeries, trackedSetDisabledItems, trackedSetOnlyItem, filterItems, seriesSelection } = useGraphAddSeries({
    availableMetricsGroups,
    visibleItemIds: enabledBuiltInItemIds,
    builtInItemIds,
    addedMetrics,
    addMetric,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    seriesColors: chartColors.series,
    seriesColorSlots,
    setSeriesColorSlots,
  })

  // Filters out a stale id, or noDataAddedCount keeps its "picked but has no data" notice up forever.
  const resolvableAddedMetrics = useMemo(
    () => getResolvableAddedMetrics(addedMetrics, selectedTemplate, modelMetadata, null),
    [addedMetrics, selectedTemplate, modelMetadata],
  )

  const intradayBatchId = intradayBatchMetadata?.id

  // The panel's date override carries its own period; the dashboard's raw period would
  // collapse an overridden multi-day window whenever the dashboard itself sits on 1D.
  const effectivePeriod = isDateOverrideActive && dateOverride ? dateOverride.period : period
  // Applied only in intraday mode: a historical 1D graph keeps its two-point window
  // (previous modeled date + end date), unlike the single-value table templates.
  const apiStartDate = isIntradayMode
    ? getPerformanceApiStartDate({ period: effectivePeriod, isIntradayMode, latestDate, performanceStartDate, performanceEndDate })
    : performanceStartDate

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined, legendClickedDate?: string) => {
    if (!panelId || !focusedItemId) return

    const { selectedCategory: drilldownCategory, selectedFactor, clickedItemId } = resolvePerformanceDrilldownSeed({
      focusedItemId,
      selectedCategory,
      categories,
      factorsMetadata,
    })

    // legendClickedDate (the legend-value path, no clickPosition) falls in behind clickPosition.date.
    const snapshotDate = clickPosition?.date ?? legendClickedDate

    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.performance",
      title: "Asset View: Performance",
      view: "performance",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate: apiStartDate,
        endDate: snapshotDate ?? performanceEndDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedCategory: drilldownCategory,
        selectedFactor,
        clickedDate: snapshotDate,
        clickedItemId,
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, categories, factorsMetadata, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, apiStartDate, performanceEndDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  // Same intent as a chart-dot click, seeded with the series' own last date — the legend has no click coordinates.
  const handleLegendValueClick = useCallback((focusedItemId: string, legendClickedDate?: string) => {
    handleDotClick(undefined, focusedItemId, legendClickedDate)
  }, [handleDotClick])

  const gatherers = useMemo(() => {
    return [createPerformanceCumulativeMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      apiStartDate,
      performanceEndDate,
      isIntradayMode,
      { isRfrAttributionEnabled, batchId: intradayBatchId, lastIntradayBatchId, intradayEnabled: instrumentsMetadata?.original?.intradayEnabled },
    )]
  }, [modelMetadata, instrumentWithSegment, apiStartDate, performanceEndDate, baseWithSegment, isIntradayMode, isRfrAttributionEnabled, intradayBatchId, lastIntradayBatchId, instrumentsMetadata])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, apiStartDate, performanceEndDate, intradayPricingAsOf, lastIntradayBatchId)

  const isUsingIntradayQuery = isIntradaySimulationEligible(isIntradayMode, apiStartDate, performanceEndDate, intradayBatchId, lastIntradayBatchId)
  // Never batchTrend — ModelIntradaySimulationBatch has no performance field — but the tooltip
  // still needs liveTick to keep its latest-price time header.
  const intradayChartMode = getIntradayChartMode(false, isUsingIntradayQuery)
  const noDataMessage = isUsingIntradayQuery ? "Intraday performance data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={(data: unknown[]) => transformPerformanceData(data, categories)}
    combine={undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate: performanceEndDate,
      startDate,
      setItems: trackedSetDisabledItems,
      setOnlyItem: trackedSetOnlyItem,
      enableItems,
      loadingMessage: "Loading performance data...",
      errorMessage: "Failed to load performance data. Please try again.",
      emptyMessage: "No performance data available.",
      onDotClick: handleDotClick,
      onLegendValueClick: handleLegendValueClick,
      noDataMessage,
      isOneDayMode: isUsingIntradayQuery,
      intradayChartMode,
      legendPlacement: "right",
      addSeriesMetrics,
      alreadyShownMetricIds,
      onAddSeries,
      seriesSelection,
      addedMetricIds: resolvableAddedMetrics,
    }}
  />
}
